import type { ReactNode } from 'react'
import { NavLink, useParams } from 'react-router'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { PageHeader } from '@/components/composites/states'
import { useCrm2Meta, useCrm2Settings, type Level } from './api'

export const LEVEL_DOT: Record<Level, string> = { green: 'bg-success', amber: 'bg-warning', red: 'bg-danger' }
export const LEVEL_SOFT: Record<Level, string> = { green: 'bg-success-soft text-success', amber: 'bg-warning-soft text-warning', red: 'bg-danger-soft text-danger' }
export const LEVEL_LABEL: Record<Level, string> = { green: 'On track', amber: 'Needs a look', red: 'Needs action' }

export const ROLE_LABEL: Record<string, string> = { sponsor: 'Sponsor', decision_maker: 'Decision maker', user: 'User', champion: 'Champion', blocker: 'Blocker', other: 'Other' }
export const KIND_LABEL: Record<string, string> = { note: 'Note', email: 'Email', call: 'Call', meeting: 'Meeting', whatsapp: 'WhatsApp', task: 'Task', stage: 'Stage' }

export function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 10_000) return `$${Math.round(n / 1000)}k`
  return `$${Math.round(n).toLocaleString('en-US')}`
}

export function shortDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function when(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** YYYY-MM-DD for a date input. */
export const dateInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '')
/** A date input's value as the API wants it, or null when empty. */
export const fromDateInput = (v: string) => (v ? `${v}T00:00:00.000Z` : null)
export const todayPlus = (days: number) => new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10)

export function Dot({ level, className }: { level: Level; className?: string }) {
  return <span className={cn('inline-block size-2 shrink-0 rounded-full', LEVEL_DOT[level], className)} aria-label={LEVEL_LABEL[level]} />
}

/** Member id → name, from the meta every page already loads. */
export function usePeopleNames() {
  const meta = useCrm2Meta()
  const map = new Map((meta.data?.people ?? []).map((p) => [p.id, p.name]))
  return (id: string | null | undefined) => (id ? (map.get(id) ?? 'Someone') : 'Nobody')
}

export const fieldClass = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm'

export function PersonSelect({ value, onChange, label, allowNone = true }: { value: string | null; onChange: (v: string | null) => void; label: string; allowNone?: boolean }) {
  const people = useCrm2Meta().data?.people ?? []
  return (
    <select aria-label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className={fieldClass}>
      {allowNone ? <option value="">Nobody</option> : <option value="" disabled>Pick someone</option>}
      {people.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  )
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint ? <span className="text-tiny text-text-muted">{hint}</span> : null}
    </label>
  )
}

/** "Staging preview" on every TapCRMv2 and TapPM header, but only off production: there it is the real thing. */
export function StagingBadge() {
  const production = useCrm2Settings().data?.production ?? true
  return production ? null : <span className="rounded-full bg-info-soft px-2 py-0.5 text-tiny font-semibold text-info">Staging preview</span>
}

/** TapCRMv2's pages share one header and a row of tabs. */
export function Crm2Frame({ title, description, actions, children }: { title: string; description: string; actions?: ReactNode; children: ReactNode }) {
  const { workspaceSlug = '' } = useParams()
  const tabs = [
    [paths.crm2(workspaceSlug), 'Today'],
    [paths.crm2Dashboard(workspaceSlug), 'Dashboard'],
    [paths.crm2Board(workspaceSlug), 'Pipelines'],
    [paths.crm2Accounts(workspaceSlug), 'Accounts'],
    [paths.crm2Contacts(workspaceSlug), 'Contacts'],
    [paths.crm2Conversations(workspaceSlug), 'Conversations'],
    [paths.crm2Outreach(workspaceSlug), 'Outreach'],
    [paths.crm2Calendar(workspaceSlug), 'Calendar'],
    [paths.crm2Settings(workspaceSlug), 'Settings'],
  ] as const
  return (
    <div>
      <PageHeader kicker="TapCRMv2" title={title} description={description} actions={actions} badge={<StagingBadge />} />
      <nav className="-mt-2 mb-4 flex flex-wrap gap-1 border-b border-border-soft" aria-label="TapCRMv2">
        {tabs.map(([to, label]) => (
          <NavLink key={to} to={to} end={label === 'Today'} className={({ isActive }) => cn('-mb-px border-b-2 px-3 py-2 text-sm', isActive ? 'border-brand font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {label}
          </NavLink>
        ))}
      </nav>
      {children}
    </div>
  )
}

export function Section({ title, count, children, empty }: { title: string; count?: number; children: ReactNode; empty?: string }) {
  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <h2 className="flex items-center gap-2 border-b border-border-soft px-4 py-2.5 text-sm font-semibold">
        {title}
        {count !== undefined ? <span className="rounded-full bg-muted px-1.5 text-tiny font-medium text-text-secondary">{count}</span> : null}
      </h2>
      {count === 0 && empty ? <p className="px-4 py-3 text-sm text-muted-foreground">{empty}</p> : children}
    </section>
  )
}

/** What the contact number field asks for: WhatsApp needs the country code. */
export const PHONE_PLACEHOLDER = '+63 917 123 4567'
export const PHONE_HINT = 'With the country code, so WhatsApp can use it.'

/** A wa.me link for a number stored with its country code; none for a local-only number. */
export const whatsappLink = (phone: string | null | undefined) => (phone && phone.startsWith('+') ? `https://wa.me/${phone.slice(1)}` : null)

/**
 * A small bordered button for anything that opens another page, a pop-up or a slide-in: an account, a person,
 * a deal, "view all". Plain coloured text was too easy to miss as clickable. Actions on a panel use <Button>.
 */
export const LINK_CHIP = 'inline-flex w-fit max-w-full items-center gap-1 truncate rounded-md border border-border bg-background px-2 py-0.5 text-xs font-medium text-brand shadow-xs transition-colors hover:border-brand hover:bg-accent'
